import { CreateTextBlockReq, DeleteTextBlockReq, UpdateReorderListReq, UpdateTextBlockReq } from '../../dto/ReqDtoRepository';
import {
  DeleteResponse,
  TextBlockListResponse,
  TextBlockResponse,
  TextWithTextBlockResponse,
} from '../../dto/ResDtoRepository';
import { createTextBlock, deleteTextBlock, getTextBlockListForOwner, reorderTextBlock } from '../../api/textBlockApi';
import { updateText } from '../../api/textListApi';
import { useTextViewStore } from '../../store/TextViewStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useImportStationTextStore } from '../../store/importStationTextStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { TextBlockData } from '../../dto/EntityRepository';
import { toUpdateTextReq } from '../../util/textTag';

interface UseTextBlockResult {
  getTextBlockListForImport: (textId: string) => Promise<void>;
  selectTextForImport: (textId: string) => Promise<void>;
  createTextBlock: (data: CreateTextBlockReq) => Promise<TextBlockData | undefined>;
  updateTextBlock: (data: UpdateTextBlockReq) => Promise<TextBlockData | undefined>;
  deleteTextBlock: (data: DeleteTextBlockReq) => Promise<void>;
  discardTextBlock: (data: DeleteTextBlockReq) => Promise<void>;
  reorderTextBlock: (data: UpdateReorderListReq) => Promise<void>;
}

export const useTextBlock = (): UseTextBlockResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setActiveTextBlockList = useTextViewStore((s) => s.setActiveTextBlockList);
  const setOnlyActiveTextBlockList = useTextViewStore((s) => s.setOnlyActiveTextBlockList);
  const afterDeleteActiveTextBlock = useTextViewStore((s) => s.afterDeleteActiveTextBlock);
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((s) => s.setDeletedTempAlert);
  const setSelectedId = useImportStationTextStore((s) => s.setSelectedId);
  const setSimpleSubList = useImportStationTextStore((s) => s.setSimpleSubList);
  const withApiHandler = useApiWrapper();

  const handleGetTextBlockListForImportSuccess = (response: TextBlockListResponse) => {
    const data = response.data;
    setSimpleSubList(data);
  };

  const handleSelectTextForImportSuccess = (
    response: TextBlockListResponse,
    textId: string,
  ) => {
    handleGetTextBlockListForImportSuccess(response);
    setSelectedId(textId);
  };

  const handleUpdateTextBlockSuccess = (
    response: TextWithTextBlockResponse,
    textBlockId: string,
    requestedTextId?: string,
  ) => {
    const data = response.data.textBlockList.find((block) => block.id === textBlockId);
    if (!data) return undefined;
    const { activeText, activeTextBlockList } = useTextViewStore.getState();
    if (requestedTextId && activeText?.id === requestedTextId) {
      const newTextBlockList = activeTextBlockList.map((tb) => tb.id === data.id ? data : tb);
      setOnlyActiveTextBlockList(newTextBlockList);
      setUpdatedTempAlert();
    }
    return data;
  };

  const handleDeleteTextBlockSuccess = (_response: DeleteResponse, textBlockId: string) => {
    console.log("deleted well");
    afterDeleteActiveTextBlock(textBlockId);
    setDeletedTempAlert();
  };

  const handleCreateTextBlockSuccess = (
    response: TextBlockResponse,
    requestedTextId: string,
  ) => {
    const data = response.data;
    const { activeText, activeTextBlockList } = useTextViewStore.getState();
    if (activeText?.id === requestedTextId) {
      setOnlyActiveTextBlockList([...activeTextBlockList, { ...data, hasChanged: false }]);
    }
    return data;
  };

  const handleReorderTextBlockSuccess = (response: TextBlockListResponse) => {
    const data = response.data;
    setActiveTextBlockList(data);
  };

  const handleTextBlockRequest = async (
    action:
      | 'get list'
      | 'get list and select'
      | 'delete'
      | 'reorder',
    data: DeleteTextBlockReq | UpdateReorderListReq | string
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete': {
          const deleteReq = data as DeleteTextBlockReq;
          await runCurrentArchiveRequest(
            aui,
            () => deleteTextBlock(aui, deleteReq),
            response => handleDeleteTextBlockSuccess(response, deleteReq.textBlockId),
          );
          break;
        }
        case 'reorder':
          await runCurrentArchiveRequest(
            aui,
            () => reorderTextBlock(aui, data as UpdateReorderListReq),
            handleReorderTextBlockSuccess,
          );
          break;
        case 'get list':
          await runCurrentArchiveRequest(
            aui,
            () => getTextBlockListForOwner(aui, data as string),
            handleGetTextBlockListForImportSuccess,
          );
          break;
        case 'get list and select': {
          const textId = data as string;
          await runCurrentArchiveRequest(
            aui,
            () => getTextBlockListForOwner(aui, textId),
            response => handleSelectTextForImportSuccess(response, textId),
          );
          break;
        }
        default:
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const handleTextBlockMutation = async (
    action: 'create' | 'update',
    data: CreateTextBlockReq | UpdateTextBlockReq,
  ): Promise<TextBlockData | undefined> => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    if (action === 'create') {
      const createReq = data as CreateTextBlockReq;
      let responseData: TextBlockData | undefined;
      const appliedData = await withApiHandler(
        () => runCurrentArchiveRequest(
          aui,
          async () => {
            const response = await createTextBlock(aui, createReq);
            responseData = response.data;
            return response;
          },
          response => handleCreateTextBlockSuccess(response, createReq.textId),
        ),
        [aui, action, data],
      );
      return appliedData ?? responseData;
    }

    const updateReq = data as UpdateTextBlockReq;
    const requestedText = useTextViewStore.getState().activeText;
    if (!requestedText) return;
    const requestedTextId = requestedText.id;
    const updateReqWithText = toUpdateTextReq(requestedText, {
      includeTagIds: false,
      textBlocks: [updateReq],
    });
    let responseData: TextBlockData | undefined;
    const appliedData = await withApiHandler(
      () => runCurrentArchiveRequest(
        aui,
        async () => {
          const response = await updateText(aui, updateReqWithText);
          responseData = response.data.textBlockList.find(
            (block) => block.id === updateReq.id,
          );
          return response;
        },
        response => handleUpdateTextBlockSuccess(
          response,
          updateReq.id,
          requestedTextId,
        ),
      ),
      [aui, action, data],
    );
    return appliedData ?? responseData;
  };

  const getTextBlockListHandler = (textId: string) => handleTextBlockRequest('get list', textId);
  const selectTextForImportHandler = (textId: string) =>
    handleTextBlockRequest('get list and select', textId);
  const createTextBlockHandler = (data: CreateTextBlockReq) => handleTextBlockMutation('create', data);
  const updateTextBlockHandler = (data: UpdateTextBlockReq) => handleTextBlockMutation('update', data);
  const deleteTextBlockHandler = (data: DeleteTextBlockReq) => handleTextBlockRequest('delete', data);
  const discardTextBlockHandler = async (data: DeleteTextBlockReq) => {
    if (!pageOwnerAui) return;
    try {
      await deleteTextBlock(pageOwnerAui, data);
      afterDeleteActiveTextBlock(data.textBlockId);
    } catch {
      // 업로드 실패 후 생성된 빈 블록을 정리하는 best-effort rollback이다.
    }
  };
  const reorderTextBlockHandler = (data: UpdateReorderListReq) => handleTextBlockRequest('reorder', data);

  return {
    getTextBlockListForImport: getTextBlockListHandler,
    selectTextForImport: selectTextForImportHandler,
    createTextBlock: createTextBlockHandler,
    updateTextBlock: updateTextBlockHandler,
    deleteTextBlock: deleteTextBlockHandler,
    discardTextBlock: discardTextBlockHandler,
    reorderTextBlock: reorderTextBlockHandler,
  };
};
